'use client'

import { useState, memo } from 'react'
import {
  UserIcon,
  BookOpenIcon,
  PencilLineIcon,
  CalendarBlankIcon,
  BookmarkSimpleIcon,
  TagIcon,
} from '@phosphor-icons/react'
import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import { ImageWithFallback } from '@/ui/image-with-fallback'
import { EditFormActions } from '@/feed/row-chrome'
import { DeleteButton } from '@/ui/delete-button'
import { EditButton } from '@/ui/edit-button'
import { SourceLink } from '@/feed/row-chrome'
import { MarkdownContent } from '@/ui/markdown-content'
import { formatRelativeTime } from '@/lib/date'
import {
  ExpandableRow,
  RowHeader,
  RowContent,
  RowActions,
  PeopleList,
  TopicBadges,
  MetaTagsRow,
  MetaChip,
} from '@/feed/row-chrome'
import type { RowProps } from '../types'
import type { BookEntry } from './meta'
import type { MediaDraft } from './write-spec'

interface BookMetadata {
  coverUrl?: string
  author?: string
  year?: number
  description?: string
  status?: string
}

type BookRowProps = RowProps<BookEntry, MediaDraft>

export const BookRow = memo(
  function BookRow({
    entry,
    isExpanded,
    onToggle,
    onDelete,
    onSave,
    isDeleting,
    isSaving,
  }: BookRowProps) {
    const metadata = entry.metadata as BookMetadata
    const people = entry.people || []
    const [isEditing, setIsEditing] = useState(false)
    const [editTitle, setEditTitle] = useState(entry.title)
    const [editAuthor, setEditAuthor] = useState(metadata.author || '')
    const [editYear, setEditYear] = useState(metadata.year?.toString() || '')
    const [editDescription, setEditDescription] = useState(
      metadata.description || '',
    )
    const [editTopics, setEditTopics] = useState(
      entry.topics?.map((t) => t.name).join(', ') || '',
    )
    const [editNotes, setEditNotes] = useState(entry.content || '')
    const [editStatus, setEditStatus] = useState<
      'want_to_read' | 'reading' | 'read'
    >(
      (metadata.status as 'want_to_read' | 'reading' | 'read') ||
        'want_to_read',
    )

    const handleStartEdit = (e: React.MouseEvent) => {
      e.stopPropagation()
      setIsEditing(true)
      setEditTitle(entry.title)
      setEditAuthor(metadata.author || '')
      setEditYear(metadata.year?.toString() || '')
      setEditDescription(metadata.description || '')
      setEditTopics(entry.topics?.map((t) => t.name).join(', ') || '')
      setEditNotes(entry.content || '')
      setEditStatus(
        (metadata.status as 'want_to_read' | 'reading' | 'read') ||
          'want_to_read',
      )
    }

    const handleCancelEdit = () => {
      setIsEditing(false)
    }

    const handleSave = async () => {
      const topicsArray = editTopics
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
      await onSave({
        title: editTitle,
        author: editAuthor || null,
        year: editYear ? parseInt(editYear, 10) : null,
        description: editDescription || null,
        topics: topicsArray.length > 0 ? topicsArray : undefined,
        notes: editNotes || null,
        status: editStatus,
      })
      setIsEditing(false)
    }

    const header = (
      <RowHeader
        onToggle={onToggle}
        isExpanded={isExpanded}
        icon={
          <BookOpenIcon
            className="text-entry-book h-5 w-5 sm:h-4 sm:w-4"
            weight="duotone"
          />
        }
        title={entry.title}
        titleSuffix={
          entry.topics &&
          entry.topics.length > 0 && (
            <span className="hidden sm:inline">
              <TopicBadges topics={entry.topics} maxDisplay={2} />
            </span>
          )
        }
        metadata={
          <span className="text-muted-foreground/50 hidden text-xs sm:inline">
            {formatRelativeTime(entry.createdAt)}
          </span>
        }
      />
    )

    return (
      <ExpandableRow isExpanded={isExpanded} entryType="book" header={header}>
        <RowContent
          showImage
          image={
            <ImageWithFallback
              src={entry.imageUrl}
              alt={entry.title}
              aspectRatio="portrait"
              fallbackIcon={BookOpenIcon}
              fallbackColor="text-entry-book"
            />
          }
        >
          {isEditing ? (
            <BookEditForm
              editTitle={editTitle}
              setEditTitle={setEditTitle}
              editAuthor={editAuthor}
              setEditAuthor={setEditAuthor}
              editYear={editYear}
              setEditYear={setEditYear}
              editDescription={editDescription}
              setEditDescription={setEditDescription}
              editTopics={editTopics}
              setEditTopics={setEditTopics}
              editStatus={editStatus}
              setEditStatus={setEditStatus}
              editNotes={editNotes}
              setEditNotes={setEditNotes}
              isSaving={isSaving}
              onCancel={handleCancelEdit}
              onSave={handleSave}
            />
          ) : (
            <>
              {metadata.description && (
                <p className="text-muted-foreground mb-2 line-clamp-2 text-sm sm:line-clamp-3">
                  {metadata.description}
                </p>
              )}

              {entry.content && (
                <MarkdownContent className="font-body-serif text-foreground/60 mb-2 text-sm leading-relaxed italic">
                  {entry.content}
                </MarkdownContent>
              )}

              {/* Read-only meta chips */}
              <MetaTagsRow className="mb-2">
                {metadata.author && (
                  <MetaChip icon={<PencilLineIcon className="h-3 w-3" />}>
                    {metadata.author}
                  </MetaChip>
                )}
                {metadata.year && (
                  <MetaChip icon={<CalendarBlankIcon className="h-3 w-3" />}>
                    {metadata.year}
                  </MetaChip>
                )}
                {metadata.status && (
                  <MetaChip icon={<BookmarkSimpleIcon className="h-3 w-3" />}>
                    {metadata.status === 'read'
                      ? 'Read'
                      : metadata.status === 'reading'
                        ? 'Reading'
                        : 'Want to read'}
                  </MetaChip>
                )}
                {entry.topics &&
                  entry.topics.length > 0 &&
                  entry.topics.map((topic) => (
                    <MetaChip
                      key={topic.id}
                      icon={<TagIcon className="h-3 w-3" />}
                    >
                      {topic.name}
                    </MetaChip>
                  ))}
              </MetaTagsRow>

              {people.length > 0 && (
                <div className="mb-2">
                  <PeopleList
                    people={people}
                    icon={<UserIcon className="h-3 w-3" />}
                  />
                </div>
              )}

              <div className="flex items-center gap-3">
                <SourceLink entry={entry} />
                <div className="flex-1" />
                <RowActions>
                  <EditButton
                    onClick={handleStartEdit}
                    variant="icon"
                    className="opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
                  />
                  <DeleteButton
                    onClick={onDelete}
                    isDeleting={isDeleting}
                    variant="icon"
                    className="opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
                  />
                </RowActions>
              </div>
            </>
          )}
        </RowContent>
      </ExpandableRow>
    )
  },
  // Memoization comparison
  (prevProps, nextProps) => {
    return (
      prevProps.entry.id === nextProps.entry.id &&
      prevProps.entry.title === nextProps.entry.title &&
      prevProps.isExpanded === nextProps.isExpanded &&
      prevProps.isDeleting === nextProps.isDeleting &&
      prevProps.isSaving === nextProps.isSaving &&
      prevProps.entry.url === nextProps.entry.url
    )
  },
)

interface BookEditFormProps {
  editTitle: string
  setEditTitle: (v: string) => void
  editAuthor: string
  setEditAuthor: (v: string) => void
  editYear: string
  setEditYear: (v: string) => void
  editDescription: string
  setEditDescription: (v: string) => void
  editTopics: string
  setEditTopics: (v: string) => void
  editStatus: 'want_to_read' | 'reading' | 'read'
  setEditStatus: (v: 'want_to_read' | 'reading' | 'read') => void
  editNotes: string
  setEditNotes: (v: string) => void
  isSaving: boolean
  onCancel: () => void
  onSave: () => void
}

function BookEditForm({
  editTitle,
  setEditTitle,
  editAuthor,
  setEditAuthor,
  editYear,
  setEditYear,
  editDescription,
  setEditDescription,
  editTopics,
  setEditTopics,
  editStatus,
  setEditStatus,
  editNotes,
  setEditNotes,
  isSaving,
  onCancel,
  onSave,
}: BookEditFormProps) {
  return (
    <div className="space-y-3">
      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Title
        </label>
        <Input
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          className="h-8 text-sm"
          placeholder="Book title"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-muted-foreground mb-1 block text-xs">
            Author
          </label>
          <Input
            value={editAuthor}
            onChange={(e) => setEditAuthor(e.target.value)}
            className="h-8 text-sm"
            placeholder="Author name"
          />
        </div>
        <div>
          <label className="text-muted-foreground mb-1 block text-xs">
            Year
          </label>
          <Input
            type="number"
            value={editYear}
            onChange={(e) => setEditYear(e.target.value)}
            className="h-8 text-sm"
            placeholder="2024"
          />
        </div>
      </div>

      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Description
        </label>
        <Textarea
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          className="min-h-[60px] resize-none text-sm"
          placeholder="Book description..."
        />
      </div>

      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Topics (comma-separated)
        </label>
        <Input
          value={editTopics}
          onChange={(e) => setEditTopics(e.target.value)}
          className="h-8 text-sm"
          placeholder="Fiction, Science, History"
        />
      </div>

      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Status
        </label>
        <select
          value={editStatus}
          onChange={(e) =>
            setEditStatus(e.target.value as 'want_to_read' | 'reading' | 'read')
          }
          className="border-input bg-background text-foreground h-8 w-full rounded-md border px-2 text-sm"
        >
          <option value="want_to_read">Want to read</option>
          <option value="reading">Reading</option>
          <option value="read">Read</option>
        </select>
      </div>

      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Notes
        </label>
        <Textarea
          value={editNotes}
          onChange={(e) => setEditNotes(e.target.value)}
          className="min-h-[60px] resize-none text-sm"
          placeholder="Your personal notes..."
        />
      </div>

      <EditFormActions
        isSaving={isSaving}
        onCancel={onCancel}
        onSave={onSave}
        disabled={!editTitle.trim()}
      />
    </div>
  )
}
